import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { ButtonVariant, StyledButton } from './Button.styles';
import { getUrlWithTrailingSlash, isExternalUrl } from '@utils/urlHelpers.util';
import { useGatsbyLinkProps } from '@hooks/useGatsbyLinkProps';
import { JoykitIcon } from '@components/JoykitIcon';
import { JoykitIconVariant } from '@components/JoykitIcon/JoykitIconsSet';
import { JoykitIconSize } from '@components/themes/createTheme.util';

const { segment } = require('../../../static/js/joy');

interface ButtonBaseProps {
  forwardedAs?: 'button';
  href?: never;
  target?: never;
  to?: never;
  type?: 'submit';
  skipRedirectWithTelemetry?: boolean;
}
interface AnchorBaseProps {
  forwardedAs: 'a';
  href: string;
  target?: string;
  to?: never;
  type?: 'submit';
  skipRedirectWithTelemetry?: boolean;
}
interface GatsbyLinkBaseProps {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  forwardedAs: React.ComponentType<any>;
  href?: never;
  target?: never;
  to: string;
}

interface CTAAbstractButtonProps {
  id: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  telemetryLabel?: string;
}

interface CTADefaultButtonProps extends CTAAbstractButtonProps {
  variant?: ButtonVariant;
  type?: 'submit';
  skipRedirectWithTelemetry?: boolean;
  /** When true, applies full pill border-radius (9999px) that adapts to any height */
  rounded?: boolean;
  /** Icon to display on the left side of the button text */
  iconLeft?: JoykitIconVariant;
  /** Icon to display on the right side of the button text */
  iconRight?: JoykitIconVariant;
  /** Size of the icons in pixels (default: 16). Allowed values: 16, 24, 32, 40, 48, 64 */
  iconSize?: JoykitIconSize;
}

type GatsbyLinkButtonProps = GatsbyLinkBaseProps & CTADefaultButtonProps;
export type AnchorButtonProps = AnchorBaseProps & CTADefaultButtonProps;
type ButtonProps = ButtonBaseProps & CTADefaultButtonProps;

export type AllButtonProps = AnchorButtonProps | GatsbyLinkButtonProps | ButtonProps;

const CTAButtonBase = ({
  className,
  onClick,
  as,
  target,
  type,
  telemetryLabel,
  skipRedirectWithTelemetry = false,
  forwardedAs,
  iconLeft,
  iconRight,
  iconSize = 16,
  children,
  ...restProps
}: PropsWithChildren<AllButtonProps & { as?: AllButtonProps['forwardedAs'] }>) => {
  let rel;
  if (as === 'a') {
    if (isExternalUrl(restProps.href)) {
      target = target || '_blank';
      if (target === '_blank') {
        rel = 'noopener noreferrer';
      }
    }
  }
  if (restProps.to) {
    restProps.to = getUrlWithTrailingSlash(restProps.to);
  }
  const handleOnClick = (event: React.MouseEvent) => {
    if (onClick) {
      onClick(event);
    }
    if (!skipRedirectWithTelemetry) {
      segment.redirectWithTelemetry(event, undefined, telemetryLabel);
    }
  };
  return (
    <StyledButton as={as} className={className} rel={rel} target={target} {...restProps} onClick={handleOnClick}>
      {iconLeft && <JoykitIcon name={iconLeft} size={iconSize} />}
      {children}
      {iconRight && <JoykitIcon name={iconRight} size={iconSize} />}
    </StyledButton>
  );
};

const withGatsbyLink =
  (Component: React.FunctionComponent<PropsWithChildren<AllButtonProps>>) =>
  (props: PropsWithChildren<AllButtonProps & { as?: never }>) => {
    const [gatsbyLinkProps] = useGatsbyLinkProps(props);
    return <Component {...gatsbyLinkProps} />;
  };

const Button = styled(withGatsbyLink(CTAButtonBase))<PropsWithChildren<AllButtonProps & { as?: never }>>``;

Button.defaultProps = {
  variant: 'primary',
  forwardedAs: 'button',
};

Button.displayName = 'Button';

export { Button };
